<script setup>
import { computed } from 'vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import LabelPill from 'dashboard/components-next/label/LabelPill.vue';

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  subDescription: { type: String, default: '' },
  icon: { type: Object, required: true },
  iconClass: { type: String, default: '' },
  isActive: { type: Boolean, default: false },
  activeCardClass: { type: String, default: '' },
  labelPills: { type: Array, default: () => [] },
});

defineEmits(['click']);

const ICON_SIZE = 22;
const MAX_TITLE_LENGTH = 25;

const displayedTitle = computed(() => {
  if (props.title.length <= MAX_TITLE_LENGTH) return props.title;

  return `${props.title.slice(0, MAX_TITLE_LENGTH)}...`;
});
</script>

<template>
  <div
    class="w-[13.125rem] overflow-hidden rounded-xl bg-n-solid-1 shadow-sm transition-shadow hover:shadow-md dark:bg-n-solid-2"
    :class="
      isActive ? activeCardClass : 'border border-n-weak dark:border-n-strong'
    "
  >
    <button
      type="button"
      class="flex w-full flex-col p-0 text-start outline-none"
      :title="title"
      @click="$emit('click')"
    >
      <div class="flex w-full items-center gap-2 px-3 py-2.5">
        <HugeIcon
          :icon="icon"
          :size="ICON_SIZE"
          class="pointer-events-none shrink-0"
          :class="iconClass"
        />
        <span
          class="min-w-0 truncate text-xs font-medium leading-snug text-n-slate-12"
        >
          {{ displayedTitle }}
        </span>
      </div>
      <div
        v-if="description || subDescription || labelPills.length"
        role="separator"
        class="block h-px w-full shrink-0 bg-n-weak dark:bg-n-strong"
        aria-hidden="true"
      />
      <div
        v-if="description || subDescription || labelPills.length"
        class="flex w-full flex-col gap-1.5 px-3 py-2.5"
      >
        <p
          v-if="description"
          class="m-0 line-clamp-2 text-xs leading-snug text-n-slate-12"
          :class="{ 'min-h-[2rem]': subDescription }"
        >
          {{ description }}
        </p>
        <p
          v-if="subDescription"
          class="m-0 line-clamp-2 text-xs leading-snug text-n-slate-10"
        >
          {{ subDescription }}
        </p>
        <div v-if="labelPills.length" class="flex flex-wrap gap-1">
          <LabelPill
            v-for="label in labelPills"
            :key="label.title"
            :label="label"
            dense
          />
        </div>
      </div>
    </button>
  </div>
</template>
